import { useContext, useEffect, useMemo, useState } from 'react'
import HeroCard from '../sections/HeroCard'
import { generateDataHash } from '../../helpers/contentHash'
import { MarkupContext } from '../../providers/Markup'
import { transformPaddingToTailwind } from '../../helpers/style'

function HeroCards({ heroProps, componentPadding: componentPaddingProp, textAlignment, parent }) {
  const { createMarkup } = useContext(MarkupContext)
  const componentPadding = componentPaddingProp ?? transformPaddingToTailwind(heroProps.componentPadding, true)

  const validCardItems = useMemo(
    () =>
      (heroProps.cardItems || []).filter((card) => {
        const hasTitle = card.title && card.title.trim() !== ''
        const hasDescription = card.description && card.description.trim() !== ''
        const hasDesktopImage = card.desktopImage !== null
        const hasMobileImage = card.mobileImage !== null
        const hasCta = card.ctaLinkItem !== null
        return hasTitle || hasDescription || hasDesktopImage || hasMobileImage || hasCta
      }),
    [heroProps.cardItems],
  )

  const hasCardItems = validCardItems.length > 0
  const [cardColumns, setCardColumns] = useState('justify-between')
  const [cardStartCols, setCardStartCols] = useState('col-start-1')
  const [cardSpanCols, setCardSpanCols] = useState('col-span-4')

  useEffect(() => {
    let cols = 'justify-between'
    const cardItemsLength = validCardItems.length
    switch (cardItemsLength) {
      case 6:
      case 5:
        cols = 'justify-between'
        break
      case 4:
      case 3:
      case 2:
        cols = 'justify-evenly'
        break
      case 1:
        cols = 'justify-center'
        break
      default:
        cols = 'justify-between'
    }
    setCardColumns(cols)

    let start = 'col-start-1'
    let span = 'col-span-4'
    switch (cardItemsLength) {
      case 6:
        start = 'col-start-1 md-col-start-1'
        span = 'col-span-2 md:col-span-4'
        break
      case 5:
        start = 'col-start-1 md:col-start-1 2xl:col-start-3'
        span = 'col-span-2 md:col-span-4 2xl:col-span-4 max-md:last:col-start-2 md:max-2xl:nth-last-2:col-start-3  '
        break
      case 4:
        start = 'col-start-1 md:col-start-1 2xl:col-start-5'
        span = 'col-span-2 md:col-span-4 2xl:col-span-4 md:max-2xl:last:col-start-5 '
        break
      case 3:
        start = 'col-start-1 md-col-start-1 2xl:col-start-7'
        span = 'col-span-2 md:col-span-4 2xl:col-span-4 max-md:last:col-start-2 '
        break
      case 2:
        start = 'col-start-1 md:col-start-3 2xl:col-start-9'
        span = 'col-span-2 md:col-span-4 2xl:col-span-4'
        break
      case 1:
        start = 'col-start-2 md:col-start-5 2xl:col-start-11 '
        span = 'col-span-2 md:col-span-4 2xl:col-span-4'
        break
      default:
        break
    }
    setCardStartCols(start)
    setCardSpanCols(span)
  }, [validCardItems])

  if (!hasCardItems && !heroProps.footnoteText) {
    return null
  }

  return (
    <section
      type="heroBannerCards"
      data-index={`${heroProps.idx + 0.5}`}
      className={`${heroProps.backgroundConfiguration === 'Contained' ? 'container mx-auto' : ''}`}
    >
      <div data-type="footnote" className="inner container mx-auto px-7 basis-full">
        {hasCardItems && (
          <div
            className={`grid grid-cols-4 md:grid-cols-12 2xl:grid-cols-[repeat(24,1fr)] gap-4 mx-auto max-2xl:max-w-3xl ${cardColumns} ${componentPadding.card}${parent === 'heroBannerSplit' ? ' pt-8' : ''}`}
          >
            {validCardItems.map((cardItem, i) => (
              <HeroCard
                key={generateDataHash(cardItem)}
                index={i}
                cardItem={cardItem}
                cardStartCols={cardStartCols}
                cardSpanCols={cardSpanCols}
              />
            ))}
          </div>
        )}

        {heroProps.footnoteText && (
          <div
            className={`${componentPadding.bottom} ${componentPadding.top} text-${textAlignment.align}`}
            data-rte="true"
            dangerouslySetInnerHTML={createMarkup(heroProps.footnoteText)}
          />
        )}
      </div>
    </section>
  )
}

export default HeroCards
